import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import ROUTES from "../routes";
import mainHallImg from "../assets/main_hall/main-hall-1.jpg";
import workshopImg from "../assets/workshop_room/community-room-1.jpg";
import kitchenImg from "../assets/kitchen/Kitchen-3.jpg";
import { sanityFetch, sanityImageUrl } from "../sanityClient";

interface SanityImage {
  asset: { _ref: string; _type: string };
}

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.14 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: "easeOut" as const },
  },
};

export default function AnimatedRentalSection() {
  const [cardImages, setCardImages] = useState<{
    mainMarketHall?: SanityImage;
    workshopRoom?: SanityImage;
    commercialKitchen?: SanityImage;
  }>({});

  useEffect(() => {
    sanityFetch<{
      mainMarketHall: { cardImage?: SanityImage };
      workshopRoom: { cardImage?: SanityImage };
      commercialKitchen: { cardImage?: SanityImage };
    }>(
      `{
        "mainMarketHall": *[_type == "mainMarketHall"][0]{cardImage},
        "workshopRoom": *[_type == "workshopRoom"][0]{cardImage},
        "commercialKitchen": *[_type == "commercialKitchen"][0]{cardImage}
      }`,
    ).then((data) => {
      if (data)
        setCardImages({
          mainMarketHall: data.mainMarketHall?.cardImage,
          workshopRoom: data.workshopRoom?.cardImage,
          commercialKitchen: data.commercialKitchen?.cardImage,
        });
    });
  }, []);

  const spaces = [
    {
      title: "Main Market Hall",
      desc: "A spacious, versatile hall perfect for large events, markets, and gatherings.",
      img: cardImages.mainMarketHall
        ? sanityImageUrl(cardImages.mainMarketHall)
        : mainHallImg,
      href: ROUTES.MainMarketHall,
      accent: "#c6252d",
    },
    {
      title: "Workshop Room",
      desc: "A bright, flexible space ideal for classes, workshops, and community meetups.",
      img: cardImages.workshopRoom
        ? sanityImageUrl(cardImages.workshopRoom)
        : workshopImg,
      href: ROUTES.WorkshopRoom,
      accent: "#2d6a14",
    },
    {
      title: "Commercial Kitchen",
      desc: "A fully equipped professional kitchen available for culinary entrepreneurs.",
      img: cardImages.commercialKitchen
        ? sanityImageUrl(cardImages.commercialKitchen)
        : kitchenImg,
      href: ROUTES.Kitchen,
      accent: "#2d6a14",
    },
  ];

  return (
    <section
      style={{
        background: "linear-gradient(180deg, #080f1a 0%, #0d1b2a 100%)",
        padding: "clamp(56px, 9vw, 100px) 0",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Subtle grid pattern */}
      <div className="rs-grid-overlay" />

      <div
        style={{
          maxWidth: "1160px",
          margin: "0 auto",
          padding: "0 clamp(16px, 4vw, 32px)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{
            textAlign: "center",
            marginBottom: "clamp(36px, 6vw, 60px)",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: "rgba(109,199,68,0.1)",
              border: "1px solid rgba(109,199,68,0.25)",
              borderRadius: "999px",
              padding: "9px 22px",
              color: "#6dc744",
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 700,
              fontSize: "0.78rem",
              letterSpacing: "2px",
              textTransform: "uppercase",
              marginBottom: "18px",
            }}
          >
            🏢 Available to Book
          </span>
          <h2
            style={{
              fontSize: "clamp(2rem, 6vw, 3.8rem)",
              fontWeight: 900,
              color: "#fff",
              fontFamily: "'Montserrat', 'Segoe UI', sans-serif",
              letterSpacing: "-1.5px",
              margin: "0 0 14px",
              textShadow: "0 4px 30px rgba(0,0,0,0.4)",
            }}
          >
            Rental{" "}
            <span
              style={{
                background: "linear-gradient(90deg, #6dc744, #4a8c25)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Spaces
            </span>
          </h2>
          <p
            style={{
              color: "rgba(255,255,255,0.55)",
              fontSize: "clamp(0.95rem, 2vw, 1.15rem)",
              maxWidth: "520px",
              margin: "0 auto",
              lineHeight: 1.65,
            }}
          >
            Need a venue for your next event or gathering? Explore our flexible,
            fully-equipped spaces.
          </p>
        </motion.div>

        {/* Space cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
            gap: "clamp(16px, 2.5vw, 24px)",
          }}
        >
          {spaces.map((space) => (
            <motion.div
              key={space.title}
              variants={cardVariants}
              whileHover={{ y: -8 }}
              transition={{ duration: 0.25 }}
              style={{
                position: "relative",
                borderRadius: "22px",
                overflow: "hidden",
              }}
            >
              <Link
                to={space.href}
                style={{ textDecoration: "none", display: "block" }}
              >
                {/* Background image */}
                <div
                  style={{
                    height: "clamp(260px, 35vw, 380px)",
                    backgroundImage: `url(${space.img})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    transition: "transform 0.5s ease",
                  }}
                  className="rs-card-img"
                />

                {/* Gradient overlay */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(to top, rgba(4,10,22,0.95) 0%, rgba(4,10,22,0.5) 50%, rgba(4,10,22,0.1) 100%)",
                  }}
                />

                {/* Accent top border */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "3px",
                    background: `linear-gradient(90deg, ${space.accent}, transparent)`,
                  }}
                />

                {/* Content */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: "clamp(20px, 3vw, 28px)",
                  }}
                >
                  <h3
                    style={{
                      color: "#fff",
                      fontSize: "clamp(1.1rem, 2.5vw, 1.35rem)",
                      fontWeight: 800,
                      fontFamily: "'Montserrat', 'Segoe UI', sans-serif",
                      margin: "0 0 8px",
                      letterSpacing: "-0.3px",
                    }}
                  >
                    {space.title}
                  </h3>
                  <p
                    style={{
                      color: "rgba(255,255,255,0.72)",
                      fontSize: "0.88rem",
                      margin: "0 0 16px",
                      lineHeight: 1.55,
                    }}
                  >
                    {space.desc}
                  </p>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      color: "#6dc744",
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      fontFamily: "'Montserrat', sans-serif",
                      letterSpacing: "0.3px",
                    }}
                  >
                    Learn More
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M2 7h10M7 2l5 5-5 5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          style={{ textAlign: "center", marginTop: "clamp(36px, 5vw, 52px)" }}
        >
          <Link
            to={ROUTES.RENTAL_SPACES}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              background: "linear-gradient(90deg, #2d6a14, #4a8c25)",
              color: "#fff",
              padding: "0.9em 2.6em",
              borderRadius: "999px",
              textDecoration: "none",
              fontWeight: 700,
              fontSize: "1rem",
              fontFamily: "'Montserrat', sans-serif",
              letterSpacing: "0.3px",
              boxShadow: "0 6px 28px rgba(74,140,37,0.3)",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            className="rs-cta-btn"
          >
            View All Rental Spaces
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 8h10M8 3l5 5-5 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        </motion.div>
      </div>

      <style>{`
        .rs-grid-overlay {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
          background-size: 48px 48px;
          pointer-events: none;
        }
        .rs-card-img {
          transition: transform 0.5s ease !important;
        }
        a:hover .rs-card-img {
          transform: scale(1.06);
        }
        .rs-cta-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 36px rgba(74,140,37,0.45) !important;
        }
      `}</style>
    </section>
  );
}
